/**
 * Recursos dos Canais (números de WhatsApp e seus templates) que o ponteiro de uma novidade sabe
 * apontar. Ver `spotlightTargets.js`.
 *
 * A tela de Canais é a mesma das Integrações (um só componente); os recursos de CANAIS ficam aqui e
 * os de integrações de terceiros, no arquivo das Integrações. Os templates não têm item de menu: a
 * pessoa chega neles pelo cartão do número, então o roteiro de todos parte do cartão. Tudo que mora
 * dentro de uma gaveta (detalhes do canal, editar perfil) fica de fora: o ponteiro some enquanto há
 * um diálogo aberto. Os ícones de editar e excluir do cartão só aparecem ao passar o mouse e não
 * são apontados pelo mesmo motivo.
 */

const CARTAO = "channels.whatsapp-card";
const CRIAR = "channels.tpl-create-button";
const GRADE = "channels.tpl-grid";
const LAPIS = "channels.tpl-edit-from-preview";

const CANAIS = "Números de WhatsApp";
const LISTA = "Templates: lista";
const EDITOR = "Templates: editor";

const abrirTemplates = { key: CARTAO, text: "Clique no cartão do seu número e depois em Ver Templates." };
const abrirEditor = [abrirTemplates, { key: CRIAR, text: "Clique em Criar Template." }];
const escolherTemplate = [abrirTemplates, { key: GRADE, text: "Clique em um template da lista." }];
const abrirEdicao = [
  abrirTemplates,
  { key: GRADE, text: "Clique em um template da lista." },
  { key: LAPIS, text: "Clique no lápis da prévia para editar." },
];

export const CHANNELS_TARGETS = [
  // ── Números conectados ──────────────────────────────────────────────────
  {
    key: "channels.new-integration",
    navKey: "channels",
    group: CANAIS,
    label: "Botão Nova Integração",
    path: "/channels",
    requires: "page",
    hint: "Conecta um novo canal (WhatsApp Cloud API, Processo Já) ou sistema à conta. É o mesmo botão da tela de Integrações.",
  },
  {
    key: CARTAO,
    navKey: "channels",
    group: CANAIS,
    label: "Cartão do número de WhatsApp",
    path: "/channels",
    requires: "data",
    hint: "O cartão de cada número conectado: clicar abre o perfil, a qualidade, os IDs e o atalho para os templates. Só existe com um número conectado.",
  },
  {
    key: "channels.number-status",
    navKey: "channels",
    group: CANAIS,
    label: "Estado do número na Meta",
    path: "/channels",
    requires: "data",
    hint: "O selo de cada cartão (Conectado, Sinalizado, Restrito, Banido) mostra se a Meta limitou o envio. Aparece com um número conectado.",
  },
  {
    key: "channels.quality-rating",
    navKey: "channels",
    group: CANAIS,
    label: "Qualidade do número",
    path: "/channels",
    requires: "data",
    hint: "O selo de qualidade (Alta, Média, Baixa) aparece depois que a Meta responde; qualidade baixa limita o alcance dos disparos.",
  },
  {
    key: "channels.processo-ja-card",
    navKey: "channels",
    group: CANAIS,
    label: "Cartão do canal Processo Já",
    path: "/channels",
    requires: "data",
    feature: "channels.app_connect",
    hint: "Ativar, editar e gerenciar o app do cliente. Só em conta com o Processo Já no plano e já provisionado.",
  },

  // ── Templates: lista ────────────────────────────────────────────────────
  {
    key: CRIAR,
    navKey: "channels",
    group: LISTA,
    label: "Botão Criar Template",
    path: "/channels",
    requires: "panel",
    hint: "Abre o editor de um novo template, que a Meta precisa aprovar antes de poder ser disparado.",
    steps: [abrirTemplates],
  },
  {
    key: "channels.tpl-filters",
    navKey: "channels",
    group: LISTA,
    label: "Buscar e filtrar templates",
    path: "/channels",
    requires: "panel",
    hint: "Busca pelo nome e filtra por situação (aprovados, pendentes, rejeitados) e categoria.",
    steps: [abrirTemplates],
  },
  {
    key: GRADE,
    navKey: "channels",
    group: LISTA,
    label: "Cartões dos templates",
    path: "/channels",
    requires: "panel",
    hint: "Cada cartão mostra nome, situação, idioma e categoria; clicar abre a prévia. Só existe com ao menos um template.",
    steps: [abrirTemplates],
  },
  {
    key: "channels.tpl-preview-panel",
    navKey: "channels",
    group: LISTA,
    label: "Prévia do template selecionado",
    path: "/channels",
    requires: "panel",
    hint: "O celular com a mensagem; dá para trocar os exemplos das variáveis e o tema da conversa. Só com um template selecionado.",
    steps: escolherTemplate,
  },
  {
    key: LAPIS,
    navKey: "channels",
    group: LISTA,
    label: "Lápis de editar na prévia",
    path: "/channels",
    requires: "panel",
    hint: "Edita o template e envia de novo para aprovação. Não aparece em template pendente de aprovação.",
    steps: escolherTemplate,
  },
  {
    key: "channels.tpl-rejection-reason",
    navKey: "channels",
    group: LISTA,
    label: "Motivo da reprovação ou aviso de pendente",
    path: "/channels",
    requires: "panel",
    hint: "A caixa vermelha com o motivo que a Meta deu, ou o aviso amarelo de template pendente. Só em template rejeitado ou pendente.",
    steps: [abrirTemplates, { key: GRADE, text: "Clique em um template rejeitado ou pendente." }],
  },
  {
    key: "channels.tpl-sync",
    navKey: "channels",
    group: LISTA,
    label: "Botão Sync",
    path: "/channels",
    requires: "panel",
    minLevel: 30,
    hint: "Traz da Meta o status mais recente dos templates, inclusive os criados fora da plataforma. Só para supervisor ou acima.",
    steps: [abrirTemplates],
  },
  {
    key: "channels.tpl-replicate",
    navKey: "channels",
    group: LISTA,
    label: "Replicar templates para outros números",
    path: "/channels",
    requires: "panel",
    hint: "O ícone de seleção liga o modo de replicar: marque templates aprovados e copie para outro número. Só com 2 ou mais números de WhatsApp.",
    steps: [abrirTemplates],
  },
  {
    key: "channels.tpl-load-more",
    navKey: "channels",
    group: LISTA,
    label: "Carregar mais templates",
    path: "/channels",
    requires: "panel",
    hint: "A lista mostra 12 por vez; o botão traz o resto. Só com mais de 12 templates.",
    steps: [abrirTemplates],
  },

  // ── Templates: editor ───────────────────────────────────────────────────
  {
    key: "channels.tpl-editor-name",
    navKey: "channels",
    group: EDITOR,
    label: "Nome do template",
    path: "/channels",
    requires: "panel",
    hint: "Só letras minúsculas, números e sublinhado; o nome não muda depois de criado.",
    steps: abrirEditor,
  },
  {
    key: "channels.tpl-editor-category",
    navKey: "channels",
    group: EDITOR,
    label: "Categoria do template",
    path: "/channels",
    requires: "panel",
    hint: "Marketing, Utilidade ou Autenticação. A categoria errada é motivo comum de reprovação e muda o custo.",
    steps: abrirEditor,
  },
  {
    key: "channels.tpl-editor-language",
    navKey: "channels",
    group: EDITOR,
    label: "Idioma do template",
    path: "/channels",
    requires: "panel",
    hint: "Português (Brasil) é o padrão e precisa bater com o texto; não muda depois de criado.",
    steps: abrirEditor,
  },
  {
    key: "channels.tpl-editor-header",
    navKey: "channels",
    group: EDITOR,
    label: "Cabeçalho do template",
    path: "/channels",
    requires: "panel",
    hint: "Opcional: nenhum, texto, imagem, vídeo ou documento no topo da mensagem.",
    steps: abrirEditor,
  },
  {
    key: "channels.tpl-editor-body",
    navKey: "channels",
    group: EDITOR,
    label: "Corpo da mensagem e variáveis",
    path: "/channels",
    requires: "panel",
    hint: "O texto principal, até 1024 caracteres, com variáveis como {{nome}} e um exemplo de cada para a Meta aprovar.",
    steps: abrirEditor,
  },
  {
    key: "channels.tpl-editor-footer",
    navKey: "channels",
    group: EDITOR,
    label: "Rodapé do template",
    path: "/channels",
    requires: "panel",
    hint: "Opcional: uma linha curta no fim da mensagem, como o nome da empresa.",
    steps: abrirEditor,
  },
  {
    key: "channels.tpl-editor-buttons",
    navKey: "channels",
    group: EDITOR,
    label: "Botões do template",
    path: "/channels",
    requires: "panel",
    hint: "Resposta rápida, URL ou telefone. Some enquanto a solicitação de permissão de chamada está ligada.",
    steps: abrirEditor,
  },
  {
    key: "channels.tpl-editor-call-permission",
    navKey: "channels",
    group: EDITOR,
    label: "Solicitação de permissão de chamada",
    path: "/channels",
    requires: "panel",
    hint: "Adiciona o botão da Meta para o contato autorizar ligações pelo WhatsApp; não combina com outros botões.",
    steps: abrirEditor,
  },
  {
    key: "channels.tpl-editor-multichannel",
    navKey: "channels",
    group: EDITOR,
    label: "Criar também em outros canais",
    path: "/channels",
    requires: "panel",
    hint: "Cria o mesmo template em todos os números de uma vez, já que a aprovação é por número. Só ao criar e com 2 ou mais números.",
    steps: abrirEditor,
  },
  {
    key: "channels.tpl-editor-replicate-children",
    navKey: "channels",
    group: EDITOR,
    label: "Replicar nas contas filhas",
    path: "/channels",
    requires: "panel",
    hint: "Só em conta parceira (com contas filhas): as filhas recebem o template automaticamente.",
    steps: abrirEditor,
  },
  {
    key: "channels.tpl-editor-preview",
    navKey: "channels",
    group: EDITOR,
    label: "Prévia ao vivo do template",
    path: "/channels",
    requires: "panel",
    hint: "A mensagem tomando forma enquanto se escreve (visualização aproximada). Só em tela larga.",
    steps: abrirEditor,
  },
  {
    key: "channels.tpl-editor-submit",
    navKey: "channels",
    group: EDITOR,
    label: "Botão Criar Template ou Salvar",
    path: "/channels",
    requires: "panel",
    hint: "Envia o template para a Meta aprovar, o que costuma levar de minutos a horas.",
    steps: abrirEditor,
  },
  {
    key: "channels.tpl-editor-delete",
    navKey: "channels",
    group: EDITOR,
    label: "Botão Excluir template",
    path: "/channels",
    requires: "panel",
    hint: "Remove o template; só aparece ao editar um template existente.",
    steps: abrirEdicao,
  },
];
